import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { andList, placeWords } from "../../../shared/goals/audience";
import type { GoalConfig } from "../../../shared/goals/config";
import type { MetaSettings } from "../../../shared/goals/meta";
import type { PlanIdea } from "../../../shared/goals/plan";
import type { AdFormat, AdPost } from "../../../shared/goals/ready";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { FlowBar, StepHead, stepLine } from "../goal/Native";
import { META_STEPS } from "../goal/steps";
import { PnRow } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { readyApi, type LaunchView, type ReadyAd, type ReadyView } from "../../lib/readyApi";
import type { GoalView } from "../../lib/goalsApi";
import type { ProductRef } from "../goal/FlowHead";
import { FORMATS, IDEA_FORMATS } from "./AdItem";
import { AdThumb } from "./AdPreview";
import { AdScreen, adPill } from "./AdScreen";
import { BudgetButton, BudgetSheet } from "./BudgetSheet";
import { LaunchFoot } from "./LaunchFoot";
import { NativeToast } from "./NativeToast";
import { PostPicker } from "./PostPicker";
import { SetupRail } from "./SetupRail";
import { useSubScreen } from "./subScreen";
import { WhereOpensSheet } from "./WhereOpensSheet";

/** What the Ads step hands its native screens: its data and its actions, the same as on a computer. */
export interface AdsCtx {
  goalId: string;
  view: GoalView;
  ready: ReadyView;
  launch: LaunchView;
  config: GoalConfig;
  settings: MetaSettings;
  product: ProductRef | null;
  opens: string;
  opensPage: string;
  /** The ads not yet on Meta. */
  ads: ReadyAd[];
  open: PlanIdea[];
  gone: PlanIdea[];
  skipped: string[];
  setSkipped: (keys: string[]) => void;
  busy: boolean;
  act: (f: () => Promise<unknown>, ok?: string) => Promise<void>;
  saveSettings: (patch: Record<string, unknown>, summary: string) => Promise<void>;
  link: string;
  setLink: (v: string) => void;
  saveLink: () => void;
  note: string;
  setNote: (v: string) => void;
  addOwn: (f: File) => void;
  addPhotos: (fs: File[]) => void;
  picking: ReadyAd | "new" | null;
  setPicking: (p: ReadyAd | "new" | null) => void;
  pickPost: (p: AdPost) => void;
  removed: string | null;
  removedAd: (id: string) => void;
  undoRemove: () => void;
  pressLaunch: () => void;
  load: () => Promise<void>;
  recordFor: string | null;
  say: (m: string, ok?: boolean) => void;
  toast: ReactNode;
}

/**
 * Ads on a phone and an iPad: where the ads open, your ads, Add an ad and the plan's ideas, as lists, with the
 * daily budget and Launch in the bottom bar. One ad opens as its own screen.
 */
export function AdsNative(c: AdsCtx) {
  const [params, setParams] = useSearchParams();
  const adId = params.get("ad");
  useSubScreen(adId);
  const [where, setWhere] = useState(false);
  const [budget, setBudget] = useState(false);
  // Why: the code a laptop shows opens this ad on the phone, ready to record its upright version.
  useEffect(() => {
    if (!c.recordFor) return;
    setParams({ ad: c.recordFor, take: "tall" }, { replace: true });
  }, [c.recordFor, setParams]);
  const all = c.ready.ads;
  const ad = adId ? all.find((a) => a.id === adId) : undefined;
  const dailyCents = c.settings.dailyCents ?? null;

  const sheets = (
    <>
      <WhereOpensSheet
        open={where}
        onClose={() => setWhere(false)}
        goalId={c.goalId}
        settings={c.settings}
        suggested={c.view.current?.plan?.meta_events?.learns_from ?? null}
        suggestedOpens={c.view.current?.plan?.meta_opens?.opens ?? null}
        link={c.link}
        setLink={c.setLink}
        saveLink={c.saveLink}
        saveSettings={c.saveSettings}
        say={c.say}
      />
      <BudgetSheet
        open={budget}
        onClose={() => setBudget(false)}
        cents={dailyCents}
        totalCents={c.config.money.budgetCents}
        maxCents={c.config.money.dailyMaxCents}
        onSave={async (cents) => {
          // Why: a change that does not go through says so itself; the bar shows the amount that went through.
          await c.saveSettings({ dailyCents: cents }, `Meta's daily budget: $${(cents / 100).toLocaleString("en-US")}`);
          return true;
        }}
      />
      <PostPicker goalId={c.goalId} productName={c.product?.name ?? "Your Page"} open={c.picking !== null} onClose={() => c.setPicking(null)} onPick={c.pickPost} />
      <NativeToast>
        {c.toast}
        <div className={c.removed ? "toast on" : "toast"} role="status" aria-live="polite" aria-hidden={!c.removed}>
          <span>Ad removed.</span>
          <button type="button" className="t-undo" onClick={c.undoRemove}>
            Undo
          </button>
        </div>
      </NativeToast>
    </>
  );

  if (adId && ad)
    return (
      <>
        <AdScreen c={c} ad={ad} recordTall={params.get("take") === "tall"} onWhere={() => setWhere(true)} />
        {sheets}
      </>
    );
  return (
    <>
      <AdsList c={c} onWhere={() => setWhere(true)} onBudget={() => setBudget(true)} dailyCents={dailyCents} />
      {sheets}
    </>
  );
}

function AdsList({ c, onWhere, onBudget, dailyCents }: { c: AdsCtx; onWhere: () => void; onBudget: () => void; dailyCents: number | null }) {
  const [adding, setAdding] = useState(false);
  const [idea, setIdea] = useState<PlanIdea | null>(null);
  const [skippedOpen, setSkippedOpen] = useState(false);
  const { goalId, settings } = c;
  const campaign = c.launch.campaign;
  const setup = !campaign && c.view.goal.state === "getting_ready";
  const launched = c.ready.ads.filter((a) => a.status === "launched");
  const list = [...launched, ...c.ads];
  const a = settings.audience;
  const who = a ? `${andList(a.places.include.map(placeWords)) || "No place yet"}, aged ${a.ages.min} to ${a.ages.max >= 65 ? "65+" : a.ages.max}` : "Not chosen yet";
  const back = setup ? { to: `/goals/${goalId}/audience`, label: "Audience" } : { to: `/goals/${goalId}`, label: "Goal" };
  const running = launched.length;
  const sub = campaign ? `${list.length} ad${list.length === 1 ? "" : "s"}, ${running} running` : list.length ? `${list.length} ad${list.length === 1 ? "" : "s"}, not live yet` : "Not live yet";

  return (
    <div className={setup ? "pn-screen ad-scr su-wiz" : "pn-screen ad-scr"}>
      {setup ? <FlowBar sub={stepLine(6, META_STEPS.length)} back={back} /> : <PhoneNav title="Ads" large back={back} sub={sub} />}
      {setup && <SetupRail goalId={goalId} step={6} />}
      <div className={setup ? "pn-stack ad-step" : "pn-stack"}>
        {setup && <StepHead title="Ads" sub="What people see. Each ad needs its video before it runs." />}
        <section>
          <div className="pn-sh">
            <h2>Where the ads open</h2>
          </div>
          <div className="pn-grp">
            <PnRow
              onClick={onWhere}
              icon={<Icon name={settings.opens === "metaform" ? "form" : "globe"} />}
              title={settings.opens === "metaform" ? "Meta's form" : "A page on your website"}
              sub={settings.opens === "metaform" ? "Sign-ups stay inside Facebook and Instagram" : c.opensPage || "Add the page"}
              chevron
            />
          </div>
          <div className="pn-grp ad-g2">
            <PnRow to={`/goals/${goalId}/audience`} icon={<Icon name="user" />} title="Who sees the ads" sub={who} wrap />
          </div>
        </section>

        <section>
          <div className="pn-sh">
            <h2>Your ads</h2>
            {list.length > 0 && <span className="ad-cnt">{list.length}</span>}
          </div>
          <div className="pn-grp">
            {list.length ? (
              list.map((x) => (
                <PnRow
                  key={x.id}
                  className="ad-adr"
                  to={`/goals/${goalId}/ads?ad=${encodeURIComponent(x.id)}`}
                  icon={<AdThumb goalId={goalId} ad={x} />}
                  title={x.words.name ?? x.words.idea ?? "Your ad"}
                  sub={
                    <>
                      <span className="ad-fmt">{FORMATS.find(([k]) => k === (x.words.format ?? "camera"))?.[1]}</span>
                      {adPill(x)}
                    </>
                  }
                  wrap
                />
              ))
            ) : (
              <PnRow icon={<Icon name="film" />} iconTone="plain" title="No ads yet" sub="Use an idea from the plan, or add your own." wrap />
            )}
          </div>
        </section>

        <section>
          <div className="pn-grp">
            <PnRow className="accent" onClick={() => setAdding(true)} icon={<Icon name="plus" />} title="Add an ad" sub="A video you have, a published post, or your own idea" chevron wrap />
          </div>
          <p className="pn-note">3 to 5 different ads work best.</p>
        </section>

        {(c.open.length > 0 || c.gone.length > 0) && (
          <section>
            <div className="pn-sh">
              <h2>Ideas from the plan</h2>
              {c.open.length > 1 && (
                <button type="button" className="pn-lnk ad-lnk" disabled={c.busy} onClick={() => void c.act(async () => Promise.all(c.open.map((x) => readyApi.usePlanIdea(goalId, x.key))))}>
                  Use all {c.open.length}
                </button>
              )}
            </div>
            <div className="pn-grp">
              {c.open.map((x) => (
                <div className="pn-row ad-idea" key={x.key}>
                  <span className="pn-ico violet">
                    <Icon name="bulb" />
                  </span>
                  <button type="button" className="m ad-idb" onClick={() => setIdea(x)}>
                    <span className="t">{x.text}</span>
                    <span className="s">{x.why}</span>
                  </button>
                  <button type="button" className="pn-btn soft sm ad-use" disabled={c.busy} onClick={() => void c.act(() => readyApi.usePlanIdea(goalId, x.key))}>
                    Use it
                  </button>
                </div>
              ))}
              {c.gone.length > 0 && <PnRow onClick={() => setSkippedOpen(true)} title="Skipped ideas" value={String(c.gone.length)} chevron />}
            </div>
          </section>
        )}
      </div>

      <LaunchFoot
        goalId={goalId}
        launch={c.launch}
        launchedAt={c.view.goal.launchedAt}
        busy={c.busy}
        budget={<BudgetButton cents={dailyCents} onOpen={onBudget} />}
        onLaunch={c.pressLaunch}
        onTurnOn={() => void c.act(() => readyApi.turnOn(goalId))}
      />

      <AddAdSheet c={c} open={adding} onClose={() => setAdding(false)} />
      <ActionSheet
        open={idea !== null}
        onClose={() => setIdea(null)}
        title={idea?.text}
        message={idea?.why}
        actions={
          idea
            ? [
                { label: "Use it", onSelect: () => void c.act(() => readyApi.usePlanIdea(goalId, idea.key)) },
                { label: "Not this one", onSelect: () => c.setSkipped([...c.skipped, idea.key]) },
              ]
            : []
        }
      />
      <ActionSheet
        open={skippedOpen}
        onClose={() => setSkippedOpen(false)}
        title="Skipped ideas"
        message="Bring one back to the plan's ideas."
        actions={c.gone.map((x) => ({ label: x.text, onSelect: () => c.setSkipped(c.skipped.filter((k) => k !== x.key)) }))}
      />
    </div>
  );
}

/** Add an ad, in a sheet: the three ways, each its own step inside it. */
function AddAdSheet({ c, open, onClose }: { c: AdsCtx; open: boolean; onClose: () => void }) {
  const [way, setWay] = useState<"own" | "idea" | null>(null);
  const [text, setText] = useState("");
  const [fmt, setFmt] = useState<AdFormat>("camera");
  const [miss, setMiss] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const photos = useRef<HTMLInputElement>(null);
  const note = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setWay(null);
    setText("");
    setMiss(false);
  }, [open]);
  const back = (
    <button type="button" className="pn-tbtn" onClick={() => setWay(null)}>
      Back
    </button>
  );
  const choose = (input: React.RefObject<HTMLInputElement | null>) => {
    if (!c.note.trim()) {
      setMiss(true);
      note.current?.focus();
      return;
    }
    input.current?.click();
  };
  if (way === "own")
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title="A video or photos"
        left={back}
        foot={
          <div className="ad-col">
            <button type="button" className="pn-btn pri wide" disabled={c.busy} onClick={() => choose(file)}>
              <Icon name="upload" />
              Choose a video or photo
            </button>
            <button type="button" className="pn-btn wide" disabled={c.busy} onClick={() => choose(photos)}>
              <Icon name="photo" />
              Choose 2 to 10 photos
            </button>
          </div>
        }
      >
        <p className="ad-shs">The agent writes the words. Two or more photos make one ad people swipe through.</p>
        <div className="pn-form">
          <input
            ref={note}
            className="pn-inp"
            value={c.note}
            placeholder="What it shows, in a line"
            aria-label="What it shows"
            aria-invalid={miss}
            onChange={(e) => {
              c.setNote(e.target.value);
              setMiss(false);
            }}
          />
        </div>
        {miss && <p className="pn-note ad-bad">Say what it shows first, in a line.</p>}
        <input
          ref={file}
          type="file"
          hidden
          accept="video/mp4,video/quicktime,image/jpeg,image/png,image/webp"
          aria-label="A video or photo"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) {
              c.addOwn(f);
              onClose();
            }
          }}
        />
        <input
          ref={photos}
          type="file"
          hidden
          multiple
          accept="image/jpeg,image/png,image/webp"
          aria-label="Photos"
          onChange={(e) => {
            const fs = [...(e.target.files ?? [])];
            e.target.value = "";
            if (fs.length) {
              c.addPhotos(fs);
              if (fs.length >= 2 && fs.length <= 10) onClose();
            }
          }}
        />
      </Sheet>
    );
  if (way === "idea")
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title="Your own idea"
        left={back}
        foot={
          <button
            type="button"
            className="pn-btn pri wide"
            disabled={c.busy}
            onClick={() => {
              if (!text.trim()) return setMiss(true);
              const line = text.trim();
              onClose();
              void c.act(() => readyApi.addIdea(c.goalId, line, fmt), "Added. The agent drafts the words.");
            }}
          >
            <Icon name="plus" />
            Add this ad
          </button>
        }
      >
        <div className="pn-form">
          <textarea
            className="pn-inp"
            rows={3}
            value={text}
            placeholder="One line, for example: the question clients ask most, answered on camera"
            aria-label="Your own idea"
            onChange={(e) => {
              setText(e.target.value);
              setMiss(false);
            }}
          />
        </div>
        {miss && <p className="pn-note ad-bad">Write the idea in a line first.</p>}
        <div className="pn-cap ad-mt">How it is made</div>
        <div className="ad-fmts" role="group" aria-label="How it is made">
          {IDEA_FORMATS.map(([k, l]) => (
            <button key={k} type="button" className={fmt === k ? "pn-chip on" : "pn-chip"} aria-pressed={fmt === k} onClick={() => setFmt(k)}>
              {l}
            </button>
          ))}
        </div>
      </Sheet>
    );
  return (
    <Sheet open={open} onClose={onClose} title="Add an ad">
      <div className="pn-grp">
        <PnRow onClick={() => setWay("own")} icon={<Icon name="film" />} title="A video or photos you already have" sub="The agent writes the words." chevron wrap />
        <PnRow
          onClick={() => {
            onClose();
            c.setPicking("new");
          }}
          icon={<Icon name="play" />}
          title="A post you already published"
          sub="Its likes and comments come with it."
          chevron
          wrap
        />
        <PnRow onClick={() => setWay("idea")} icon={<Icon name="spark" />} iconTone="violet" title="Your own idea" sub="One line, and the agent drafts the ad." chevron wrap />
      </div>
      <p className="pn-note">3 to 5 different ads work best.</p>
    </Sheet>
  );
}
